import aiChatApi from '../../api/aiChat';
import { saveTripProfile } from '../../api/tripProfile';
import editRideApi from '../../api/editRide';
import { pollJobStatus } from '../../common/pollJobStatus';
import accommodationApi from '../../api/accommodation';
import attractionApi from '../../api/attraction';
import rideInformationApi from '../../api/rideInformation';
import extraInformationApi from '../../api/extraInformationForRide';
import {
  buildPlannedDate,
  applySequentially,
  buildAttractionPayload,
  suggestedAreaIdsInRange,
  resolveAccommodationKind,
  buildRideDateUpdatePayload,
  buildDayNoteRequest,
  buildRideNoteRequest,
  buildAttractionEditBody,
  buildAccommodationEditBody,
  buildRideTextUpdatePayload,
  buildRideTitleUpdatePayload,
  rideTitleRefusal,
  buildRideLanguageUpdatePayload,
  rideLanguageRefusal,
} from '../../utils/aiChatApply';
import { toBackendDate } from '../../utils/documentImportDates';
import { buildChatHistory, describeAttachedDocuments } from '../../utils/chatDocuments';

// attractionsByDay has one entry per calendar day of the ride, empty days
// included, so a 1-indexed day number maps straight onto it.
function resolveDayDateTime(allRide, day) {
  const dayIndex = (day || 1) - 1;
  const dayData = allRide && allRide.attractionsByDay && allRide.attractionsByDay[dayIndex];
  return dayData && dayData.dateTime ? dayData.dateTime : null;
}

// What an auto-continue turn says to the model. These are never shown to the
// manager -- the panel hides synthetic user messages -- so they are model
// instructions, not UI copy, and stay in the prompt's own language (English).
// They state what the manager just did and leave the next move to the prompt:
// the backend derives gate progress from the ride itself, so the turn must not
// claim a gate is passed.
// Each ends by pinning the reply's language: nothing in the system prompt
// fixes it, and this English line is the latest user message, so without it a
// Hebrew session can come back in English -- including the stop descriptions
// the model writes into the ride, which travellers read.
const CONTINUE_IN_LANGUAGE = ' Continue in the language you have been using with the manager.';
const AUTO_CONTINUE_MESSAGES = {
  areas_applied: 'The sleeping areas you proposed are now saved on the ride. Carry on from here.' + CONTINUE_IN_LANGUAGE,
  plan_continue: 'The stops you proposed are now on the ride. Carry on from here.' + CONTINUE_IN_LANGUAGE,
  // This one ASKS for the closing trip check rather than saying "carry on".
  // Applying the hotels is what completes the build, so by the time this turn
  // reaches the backend all three gates are passed, build_started has been
  // cleared and the ride is in the EDIT lane -- where the prompt forbids an
  // unasked trip check. The check is permitted on demand in either lane, so
  // the turn asks for it outright. Without this the closing step has no
  // trigger at all.
  hotels_applied: 'The accommodation you proposed is now saved on the ride. '
    + 'Run the whole-trip check now and attach any fix cards.' + CONTINUE_IN_LANGUAGE,
};

const state = {
  messages: [],
  loading: false,
  panelOpen: false,
  tripProfile: {},
  tripProfileRideId: null,
  tripProfilePending: [],
  // True from the moment an auto-continue turn is dispatched until its reply
  // lands or fails. The loop guard: no second synthetic turn while it is set.
  autoContinueInFlight: false,
};

const getters = {
  aiChatMessages: state => state.messages,
  aiChatLoading: state => state.loading,
  aiChatPanelOpen: state => state.panelOpen,
  aiTripProfile: state => state.tripProfile,
  aiTripProfilePending: state => state.tripProfilePending,
};

const actions = {
  // A chat reply only carries the profile once a message is sent, so seed it
  // from the ride; a later reply's trip_profile overwrites the seed.
  // The stored profile is scoped to the ride it belongs to. Replies and saves
  // update it but not the ride, so re-seed only when it belongs to another ride
  // (an intentionally empty profile for this ride is kept).
  aiChatOpen({ commit, rootGetters, rootState, state }) {
    const rideId = rootState.ride.rideId;
    // A missing ride id never matches, so it re-seeds rather than silently showing nothing.
    if (rideId == null || state.tripProfileRideId !== rideId) {
      const ride = rootGetters.allRide && rootGetters.allRide.ride;
      commit('setAiTripProfile', { profile: (ride && ride.ai_trip_profile) || {}, rideId });
      commit('setAiTripProfilePending', []);
    }
    commit('setAiChatPanelOpen', true);
  },

  aiChatClose({ commit }) {
    commit('setAiChatPanelOpen', false);
  },

  // autoContinueInFlight is deliberately not cleared here: the request it
  // guards is still running and only its own finally may release the guard.
  aiChatReset({ commit }) {
    commit('setAiChatMessages', []);
    commit('setAiTripProfile', { profile: {}, rideId: null });
    commit('setAiTripProfilePending', []);
    commit('setAiChatPanelOpen', false);
  },

  // The bubble shown when the manager drops files on the chat. UI only:
  // buildChatHistory keeps it out of what the model is sent.
  aiChatAddDocumentsMessage({ commit }, { files = [] } = {}) {
    commit('addAiChatMessage', { role: 'user', kind: 'documents', content: '', files, suggestions: [] });
  },

  // Documents just went onto the ride: tell the model, without calling it. The
  // note is a synthetic turn -- hidden from the transcript, kept in history --
  // so the manager's next question is answered knowing about them. No reply is
  // requested here on purpose: that would be a paid call per attach.
  aiChatNoteAttachedDocuments({ commit }, results) {
    const content = describeAttachedDocuments(results);
    if (!content) return;
    commit('addAiChatMessage', { role: 'user', kind: 'documents_attached', synthetic: true, content, suggestions: [] });
  },

  // Payload is either the text itself or { message, synthetic }. A synthetic
  // turn is one the panel sent on the manager's behalf after they applied a
  // batch of cards; it goes through exactly this path -- same ride-id guards,
  // same history, same 60s poll -- and is only flagged so the panel can keep it
  // out of the transcript. It stays in state.messages so the next request's
  // history still explains why the assistant said what it said.
  async aiChatSend({ rootState, commit, state }, payload) {
    const { message, synthetic = false } = typeof payload === 'string'
      ? { message: payload }
      : (payload || {});
    if (state.loading || !message) return;

    const token = rootState.appauth.apptoken;
    const rideId = rootState.ride.rideId;

    const history = buildChatHistory(state.messages);

    commit('addAiChatMessage', { role: 'user', content: message, suggestions: [], synthetic });
    commit('setAiChatLoading', true);

    try {
      const { data } = await aiChatApi.sendMessage(token, rideId, message, history);
      const { status, result } = await pollJobStatus(
        () => aiChatApi.getJobStatus(token, data.job_id),
        { interval: 2000, timeout: 60000 },
      );

      if (status === 'complete' && result) {
        // The user may have navigated to another ride while this was in flight;
        // the reply text is still shown, but another ride's profile must not land here.
        if (rootState.ride.rideId === rideId) {
          commit('setAiTripProfile', { profile: result.trip_profile || {}, rideId });
          commit('setAiTripProfilePending', result.trip_profile_pending || []);
        }
        commit('addAiChatMessage', {
          role: 'assistant',
          content: result.reply || '',
          suggestions: result.suggestions || [],
          reschedules: result.reschedules || [],
          removals: result.removals || [],
          accommodation_removals: result.accommodation_removals || [],
          accommodation_updates: result.accommodation_updates || [],
          ride_date_updates: result.ride_date_updates || [],
          day_updates: result.day_updates || [],
          attraction_updates: result.attraction_updates || [],
          day_note_updates: result.day_note_updates || [],
          ride_note_updates: result.ride_note_updates || [],
          ride_text_updates: result.ride_text_updates || [],
          ride_title_updates: result.ride_title_updates || [],
          ride_language_updates: result.ride_language_updates || [],
          areas: result.areas || [],
          area_updates: result.area_updates || [],
          hotel_suggestions: result.hotel_suggestions || [],
          quick_replies: result.quick_replies || [],
          // Which lane the backend ran this turn in, and which gates were
          // behind it when it did. Auto-continue keys on both: a reply with
          // no lane -- an older reply, or a backend that does not send one --
          // never continues, which fails closed rather than billing a call.
          lane: result.lane || null,
          progress: result.progress || null,
        });
      } else {
        commit('addAiChatMessage', {
          role: 'assistant',
          content: 'Something went wrong. Please try again.',
          suggestions: [],
          reschedules: [],
        });
      }
    } catch (e) {
      commit('addAiChatMessage', {
        role: 'assistant',
        content: 'Failed to connect. Please try again.',
        suggestions: [],
        reschedules: [],
      });
    } finally {
      commit('setAiChatLoading', false);
    }
  },

  // Runs the model without the manager typing, once they have finished
  // applying a reply's cards. Three jobs, one mechanism: crossing gate 1,
  // continuing to the next area inside gate 2, and firing the closing trip
  // check after gate 3's hotels.
  //
  // THE LOOP GUARD. A synthetic turn that could cause another synthetic turn
  // would burn model calls without end, silently, on the manager's bill. Three
  // things stop that, and all three must hold:
  //   1. autoContinueInFlight, below -- one synthetic turn at a time, held for
  //      the whole round trip by the finally.
  //   2. state.loading, inside aiChatSend -- no turn of any kind overlaps.
  //   3. The caller. This action is dispatched only from a card-application
  //      handler in AiChatPanel, i.e. only ever in response to a manager
  //      action, and at most once per assistant message (autoContinuedFor
  //      there). Nothing watching the transcript may dispatch it: a reply
  //      arriving must never, by itself, produce another turn.
  // Do not add a caller without re-reading all three.
  async aiChatAutoContinue({ state, dispatch, commit }, { reason } = {}) {
    const message = AUTO_CONTINUE_MESSAGES[reason];
    if (!message) {
      console.warn('[aiChat] autoContinue — unknown reason, ignored:', reason);
      return { sent: false, skipped: 'unknown_reason' };
    }
    // Every refusal is logged, not only the send: during the walkthrough the
    // owner counts ai_chat_api calls in the network tab, and a silent non-fire
    // is otherwise indistinguishable from a path that was never reached.
    const skip = (skipped) => {
      console.log('[aiChat] autoContinue skipped —', skipped, '— wanted:', reason);
      return { sent: false, skipped };
    };
    // The manager closed the panel while the batch was being applied: they are
    // done with the chat, so nothing is sent.
    if (!state.panelOpen) return skip('panel_closed');
    if (state.autoContinueInFlight) return skip('in_flight');
    if (state.loading) return skip('busy');

    console.log('[aiChat] autoContinue —', reason);
    commit('setAiChatAutoContinueInFlight', true);
    try {
      await dispatch('aiChatSend', { message, synthetic: true });
    } finally {
      commit('setAiChatAutoContinueInFlight', false);
    }
    return { sent: true, reason };
  },

  // Saves manager edits; the backend returns the full saved profile.
  async aiChatSaveProfile({ rootState, commit, state }, { profile }) {
    const token = rootState.appauth.apptoken;
    const rideId = rootState.ride.rideId;
    try {
      const { data } = await saveTripProfile(token, rideId, profile);
      if (!data || !data.success) return { success: false };
      // Saved server-side for rideId, but if the store has moved to another ride
      // it must not show (and later re-save) this ride's profile.
      if (rootState.ride.rideId !== rideId) return { success: true, stale: true };
      commit('setAiTripProfile', { profile: data.ai_trip_profile || {}, rideId });
      const saved = Object.keys(profile || {});
      commit('setAiTripProfilePending', state.tripProfilePending.filter(p => !saved.includes(p.field)));
      return { success: true };
    } catch (e) {
      console.warn('[aiChat] saveProfile failed:', e);
      return { success: false };
    }
  },

  // "Keep mine": drop the pending proposal; the stored value is untouched.
  aiChatDismissProfileChange({ commit, state }, { field }) {
    commit('setAiTripProfilePending', state.tripProfilePending.filter(p => p.field !== field));
  },

  async aiChatAddSuggestion({ rootState, rootGetters, dispatch }, { suggestion, place }) {
    const token = rootState.appauth.apptoken;
    const rideId = rootState.ride.rideId;

    const dayDateTime = resolveDayDateTime(rootGetters.allRide, suggestion.day);
    console.log('[aiChat] addSuggestion — day:', suggestion.day, 'place:', place ? place.name : null);
    if (!dayDateTime) {
      console.warn('[aiChat] addSuggestion aborted — no such day in this ride', { suggestion });
      return;
    }

    const plannedDate = buildPlannedDate(dayDateTime, suggestion.time);
    const attractionPayload = buildAttractionPayload(suggestion, place, plannedDate);

    console.log('[aiChat] createAttraction payload:', JSON.stringify(attractionPayload), 'rideId:', rideId);
    await editRideApi.createAttraction(token, rideId, attractionPayload);
    dispatch('fetchRide');
  },

  async aiChatApproveArea({ rootState, dispatch }, area) {
    const token = rootState.appauth.apptoken;
    const rideId = rootState.ride.rideId;
    await aiChatApi.addSingleSuggestedArea(token, rideId, area);
    dispatch('fetchRide');
  },

  // A hotel the user approves is their accommodation, not an area offering, so
  // it goes through the same endpoint the accommodation form uses -- which also
  // gives us its date-range and double-booking guards. The AI's city-level area
  // card for those nights is then removed, as the chat promises it will be.
  async aiChatApproveHotel({ rootState, rootGetters, dispatch }, hotel) {
    const token = rootState.appauth.apptoken;
    const rideId = rootState.ride.rideId;

    const response = await accommodationApi.createAccommodation(token, rideId, {
      name: hotel.name,
      address: hotel.address || '',
      text: hotel.text || '',
      link: '',
      bookingConfirmation: '',
      checkIn: toBackendDate(hotel.check_in),
      checkOut: toBackendDate(hotel.check_out),
      lat: hotel.latitude,
      lng: hotel.longitude,
      contryAndCity: hotel.cuntry_and_city || '',
      place_id: hotel.place_id || '',
      voucherUrls: [],
      rate: null,
      image: '',
    });

    const code = response.data && response.data.ServerMassageCode;
    if (code !== 'success') {
      console.warn('[aiChat] approveHotel rejected by the server:', code);
      return { success: false, code };
    }

    const areaIds = suggestedAreaIdsInRange(rootGetters.allRide, hotel.check_in, hotel.check_out);
    console.log('[aiChat] approveHotel — replacing suggested areas:', areaIds);
    await applySequentially(areaIds, id => aiChatApi.deleteSuggestedAccommodation(token, id));

    dispatch('fetchRide');
    return { success: true };
  },

  async aiChatUpdateArea({ rootState, dispatch }, { accommodationId, area }) {
    const token = rootState.appauth.apptoken;
    await aiChatApi.updateSuggestedArea(token, accommodationId, area);
    dispatch('fetchRide');
  },

  async aiChatClearSingleSuggestion({ rootState, dispatch }, accommodationId) {
    const token = rootState.appauth.apptoken;
    await aiChatApi.deleteSuggestedAccommodation(token, accommodationId);
    dispatch('fetchRide');
  },

  // Removing an accommodation: a real booking and a suggestion live behind
  // different endpoints, and deleting a booking also destroys its confirmation
  // and vouchers -- so the kind is resolved from the ride, not from the AI.
  async aiChatRemoveAccommodation({ rootState, rootGetters, dispatch }, { accommodationId, accommodationType }) {
    const token = rootState.appauth.apptoken;
    const kind = resolveAccommodationKind(rootGetters.allRide, accommodationId, accommodationType);
    console.log('[aiChat] removeAccommodation — id:', accommodationId, 'ai type:', accommodationType, 'resolved:', kind);

    if (!kind) {
      console.warn('[aiChat] removeAccommodation aborted — cannot place this accommodation', { accommodationId });
      return { success: false, code: 'fail_to_update_accommodation' };
    }

    if (kind === 'booked') {
      const response = await accommodationApi.deleteAccommodation(token, accommodationId);
      const code = response.data && response.data.ServerMassageCode;
      if (code !== 'success') {
        console.warn('[aiChat] removeAccommodation rejected by the server:', code);
        return { success: false, code };
      }
    } else {
      await aiChatApi.deleteSuggestedAccommodation(token, accommodationId);
    }

    dispatch('fetchRide');
    return { success: true };
  },

  // Changing the dates, name or notes of a hotel the user has, through the
  // same endpoint as the accommodation form. Only booked stays: an area the
  // AI suggested goes through aiChatUpdateArea, so the kind is read from the
  // ride rather than trusted from the model.
  async aiChatUpdateAccommodation({ rootState, rootGetters, dispatch }, { update }) {
    const token = rootState.appauth.apptoken;
    const kind = resolveAccommodationKind(rootGetters.allRide, update.accommodation_id, null);
    const body = buildAccommodationEditBody(update);
    if (kind !== 'booked' || !body) {
      console.warn('[aiChat] updateAccommodation aborted —', kind !== 'booked' ? 'not a booked stay' : 'nothing to change', update);
      return { success: false, code: 'fail_to_update_accommodation' };
    }

    console.log('[aiChat] updateAccommodation — id:', update.accommodation_id, body);
    const response = await accommodationApi.updateAccommodation(token, update.accommodation_id, body);
    const code = response.data && response.data.ServerMassageCode;
    if (code !== 'success') {
      console.warn('[aiChat] updateAccommodation rejected:', code);
      // The update path spells its range error differently from the create
      // path, and only the create spelling has a popup.
      return {
        success: false,
        code: code === 'fail - date are not at ride range' ? 'date_are_not_in_range' : (code || 'fail_to_update_accommodation'),
      };
    }

    dispatch('fetchRide');
    return { success: true };
  },

  // Shifting the trip's start date, exactly as the edit-details screen does it.
  // The ride record is re-read rather than taken from allRide because this
  // endpoint replaces the whole record and commits the non-date fields before
  // it checks for a conflict -- a field missing here is a field wiped there.
  async aiChatUpdateRideStartDate({ rootState, dispatch }, { newStartDate, force = false }) {
    const token = rootState.appauth.apptoken;
    const rideId = rootState.ride.rideId;

    const { data } = await rideInformationApi.getRideInformation(token, rideId);
    const payload = buildRideDateUpdatePayload(data && data.rideInformation, newStartDate);
    if (!payload) {
      console.warn('[aiChat] updateRideStartDate aborted — unusable date', { newStartDate });
      return { success: false, code: 'date_are_not_in_range' };
    }

    console.log('[aiChat] updateRideStartDate — checkIn:', payload.checkIn, 'force:', force);
    const response = await rideInformationApi.updateRideInformation(token, rideId, payload, force);
    const code = response.data && response.data.ServerMassageCode;

    // Note the server's spelling of success.
    if (code !== 'secsses') {
      console.warn('[aiChat] updateRideStartDate rejected:', code);
      return { success: false, code };
    }

    dispatch('fetchRide');
    return { success: true };
  },

  // Renaming an attraction or changing its description, through the same
  // endpoint as the edit screen. Only the changed fields are sent; the
  // endpoint leaves the rest -- the location included -- as it is.
  async aiChatUpdateAttraction({ rootState, dispatch }, { update }) {
    const token = rootState.appauth.apptoken;
    const body = buildAttractionEditBody(update);
    if (!body) {
      console.warn('[aiChat] updateAttraction aborted — nothing to change', update);
      return { success: false, code: 'fail_to_update_attraction' };
    }

    const response = await attractionApi.updateAttraction(token, update.attraction_id, body);
    const code = response.data && response.data.ServerMassageCode;
    if (code !== 'success') {
      console.warn('[aiChat] updateAttraction rejected:', code);
      return { success: false, code: code || 'fail_to_update_attraction' };
    }

    dispatch('fetchRide');
    return { success: true };
  },

  // Adding, changing or removing the note on one day. The notes live in
  // rideHeader, not allRide, so that is what is read and refreshed afterwards.
  async aiChatApplyDayNote({ rootState, rootGetters, dispatch }, { update }) {
    const token = rootState.appauth.apptoken;
    const rideId = rootState.ride.rideId;
    const rideHeader = rootGetters.rideHeader;
    const request = buildDayNoteRequest(update, {
      rideId,
      dayDateTime: resolveDayDateTime(rootGetters.allRide, update.day),
      notes: rideHeader && rideHeader.ride && rideHeader.ride.rideExtraInformationByDay,
    });
    if (!request) {
      console.warn('[aiChat] applyDayNote aborted — cannot place this note', update);
      return { success: false, code: 'fail_to_update_day_note' };
    }

    console.log('[aiChat] applyDayNote —', request.kind, request.body);
    try {
      let ok;
      if (request.kind === 'add') {
        const response = await extraInformationApi.addExtraInformationForRide(token, request.body);
        // The add endpoint answers with the new id, or null when it refused.
        ok = !!(response.data && response.data.rideId);
      } else {
        const response = request.kind === 'update'
          ? await extraInformationApi.updateExtraInformationForRide(token, request.body)
          : await extraInformationApi.deleteExtraInformationForRide(token, request.body);
        ok = response.data && response.data.ServerMassageCode === 'success';
      }
      if (!ok) return { success: false, code: 'fail_to_update_day_note' };
    } catch (e) {
      // update/delete answer a 500 to a user who is not the ride owner.
      console.warn('[aiChat] applyDayNote failed:', e);
      return { success: false, code: 'fail_to_update_day_note' };
    }

    dispatch('fetchRideHeaderInformation');
    dispatch('fetchRide');
    return { success: true };
  },

  // A note on the trip as a whole -- one of the "General Information" entries
  // travellers read at the bottom of the ride page.
  //
  // Same table and same three endpoints as a day note; the only difference is
  // that no date is sent, which is what keeps the row ride-level. The list it
  // is matched against is the undated one, rideExtraInformation, because a
  // trip can hold several and update/remove are placed by id.
  //
  // Owner-only, like every other writer of these rows: update and delete
  // answer a bodyless 500 to anyone else, which axios throws on.
  async aiChatApplyRideNote({ rootState, rootGetters, dispatch }, { update }) {
    const token = rootState.appauth.apptoken;
    const rideId = rootState.ride.rideId;
    const rideHeader = rootGetters.rideHeader;
    const request = buildRideNoteRequest(update, {
      rideId,
      notes: rideHeader && rideHeader.ride && rideHeader.ride.rideExtraInformation,
    });
    if (!request) {
      console.warn('[aiChat] applyRideNote aborted — cannot place this note', update);
      return { success: false, code: 'fail_to_update_ride_note' };
    }

    console.log('[aiChat] applyRideNote —', request.kind, request.body);
    try {
      let ok;
      if (request.kind === 'add') {
        const response = await extraInformationApi.addExtraInformationForRide(token, request.body);
        // The add endpoint answers with the new id, or null when it refused.
        ok = !!(response.data && response.data.rideId);
      } else {
        const response = request.kind === 'update'
          ? await extraInformationApi.updateExtraInformationForRide(token, request.body)
          : await extraInformationApi.deleteExtraInformationForRide(token, request.body);
        ok = response.data && response.data.ServerMassageCode === 'success';
      }
      if (!ok) return { success: false, code: 'fail_to_update_ride_note' };
    } catch (e) {
      console.warn('[aiChat] applyRideNote failed:', e);
      return { success: false, code: 'fail_to_update_ride_note' };
    }

    // rideExtraInformation lives on the ride header (RideGeneralInfo renders
    // it); fetchRide follows the day-note handler so the itinerary view and
    // the header never disagree about this ride.
    dispatch('fetchRideHeaderInformation');
    dispatch('fetchRide');
    return { success: true };
  },

  // The trip description -- the manager's own text at the top of the ride
  // page. Written through update_ride_information_api, which takes partial
  // payloads: the keys it is not given it does not touch, so this sends the
  // description alone and the title, the dates and the traveller counts stay
  // as they are. Owner-only, like every other writer of that field.
  //
  // An APPEND is NOT composed here. appendText is sent as its own field and
  // joined server-side onto the stored description, the same way the
  // attraction and accommodation edit cards send theirs: the chat only ever
  // sees a copy of the description capped at 2000 characters, so a join made
  // here would silently drop everything past the cap -- and a join made from a
  // value read a moment earlier would lose whatever another tab saved in
  // between.
  //
  // The description lives on rideHeader.ride.text (RideDescription renders
  // it), so that is what is refreshed afterwards.
  async aiChatUpdateRideText({ rootState, dispatch }, { update }) {
    const token = rootState.appauth.apptoken;
    const rideId = rootState.ride.rideId;

    const payload = buildRideTextUpdatePayload(update);
    if (!payload) {
      console.warn('[aiChat] updateRideText aborted — nothing to change', update);
      return { success: false, code: 'fail_to_update_ride_text' };
    }

    console.log('[aiChat] updateRideText — rideId:', rideId, 'replace:', !!payload.text, 'append:', !!payload.appendText);
    try {
      const response = await rideInformationApi.updateRideInformation(token, rideId, payload);
      // Note the server's spelling of success. 'invalid_text' is its refusal of
      // text that sanitised away to nothing, and it is passed through as
      // itself: the generic failure copy blames permissions, which would send
      // the manager looking in the wrong place.
      const code = response.data && response.data.ServerMassageCode;
      if (code !== 'secsses') {
        console.warn('[aiChat] updateRideText rejected:', code);
        return { success: false, code: code === 'invalid_text' ? 'invalid_text' : 'fail_to_update_ride_text' };
      }
    } catch (e) {
      // The endpoint answers a 500 with no body to a user who is not the ride
      // owner, which axios throws on.
      console.warn('[aiChat] updateRideText failed:', e);
      return { success: false, code: 'fail_to_update_ride_text' };
    }

    dispatch('fetchRideHeaderInformation');
    return { success: true };
  },

  // The trip's NAME -- what the manager sees in their trip list, what the
  // travellers read at the top of the ride, what the brochure and the trip
  // reminders are titled. Written through the same partial
  // update_ride_information_api payload as the description, carrying { title }
  // alone, so the description, the dates and the traveller counts are not
  // touched. Owner-only, like every other writer of that record.
  //
  // A rename has no undo and no append half: the old name is gone once this
  // returns, which is why the card shows the current title beside the proposed
  // one and the manager presses on that.
  async aiChatUpdateRideTitle({ rootState, dispatch }, { update }) {
    const token = rootState.appauth.apptoken;
    const rideId = rootState.ride.rideId;

    const payload = buildRideTitleUpdatePayload(update);
    if (!payload) {
      // Which refusal it was decides what the manager is told: 'title_too_long'
      // is the model's fault and worth naming, while the generic copy blames
      // permissions and would send them looking in the wrong place.
      const refusal = rideTitleRefusal(update);
      console.warn('[aiChat] updateRideTitle aborted —', refusal, update);
      return { success: false, code: refusal || 'invalid_title' };
    }

    console.log('[aiChat] updateRideTitle — rideId:', rideId, 'title:', payload.title);
    try {
      const response = await rideInformationApi.updateRideInformation(token, rideId, payload);
      // Note the server's spelling of success. 'invalid_title' is its refusal
      // of a title that sanitised away to nothing, and it is passed through as
      // itself for the same reason as above.
      const code = response.data && response.data.ServerMassageCode;
      if (code !== 'secsses') {
        console.warn('[aiChat] updateRideTitle rejected:', code);
        const known = ['invalid_title', 'title_too_long'];
        return { success: false, code: known.includes(code) ? code : 'fail_to_update_ride_title' };
      }
    } catch (e) {
      // The endpoint answers a 500 with no body to a user who is not the ride
      // owner, which axios throws on.
      console.warn('[aiChat] updateRideTitle failed:', e);
      return { success: false, code: 'fail_to_update_ride_title' };
    }

    // The title is rendered from the ride header (the page heading, or the
    // poster design's title), which is what this refreshes -- the same place the
    // description card refreshes for the same reason.
    dispatch('fetchRideHeaderInformation');
    return { success: true };
  },

  // The language the trip is written in. Same partial payload as the title,
  // carrying { language } alone. Refreshing the ride header is what makes the
  // ride page pick it up -- and, when it flips the page between Hebrew and
  // English, reload; the card has already warned the manager of that.
  async aiChatUpdateRideLanguage({ rootState, dispatch }, { update }) {
    const token = rootState.appauth.apptoken;
    const rideId = rootState.ride.rideId;

    const payload = buildRideLanguageUpdatePayload(update);
    if (!payload) {
      const refusal = rideLanguageRefusal(update);
      console.warn('[aiChat] updateRideLanguage aborted —', refusal, update);
      return { success: false, code: refusal || 'invalid_language' };
    }

    console.log('[aiChat] updateRideLanguage — rideId:', rideId, 'language:', payload.language);
    try {
      const response = await rideInformationApi.updateRideInformation(token, rideId, payload);
      const code = response.data && response.data.ServerMassageCode;
      if (code !== 'secsses') {
        console.warn('[aiChat] updateRideLanguage rejected:', code);
        return { success: false, code: code === 'invalid_language' ? code : 'fail_to_update_ride_language' };
      }
    } catch (e) {
      // Bodyless 500 for a user who is not the ride owner, as for the title.
      console.warn('[aiChat] updateRideLanguage failed:', e);
      return { success: false, code: 'fail_to_update_ride_language' };
    }

    dispatch('fetchRideHeaderInformation');
    return { success: true };
  },

  async aiChatRemoveAttraction({ rootState, dispatch }, { attractionId }) {
    const token = rootState.appauth.apptoken;
    const rideId = rootState.ride.rideId;
    console.log('[aiChat] removeAttraction — attractionId:', attractionId, 'rideId:', rideId);
    await editRideApi.removeAttractionFromRide(token, rideId, attractionId);
    dispatch('fetchRide');
  },

  async aiChatReschedule({ rootState, rootGetters, dispatch }, { attractionId, newDay, newTime }) {
    const token = rootState.appauth.apptoken;
    const rideId = rootState.ride.rideId;

    const dayDateTime = resolveDayDateTime(rootGetters.allRide, newDay);
    console.log('[aiChat] reschedule — attractionId:', attractionId, 'newDay:', newDay, 'newTime:', newTime);
    if (!dayDateTime) {
      console.warn('[aiChat] reschedule aborted — no such day in this ride', { newDay });
      return;
    }

    const plannedDate = buildPlannedDate(dayDateTime, newTime);
    console.log('[aiChat] reschedule plannedDate:', plannedDate);
    await editRideApi.updateAttractionRideTime(token, rideId, attractionId, plannedDate);
    dispatch('fetchRide');
  },
};

const mutations = {
  setAiChatMessages: (state, messages) => { state.messages = messages; },
  setAiChatLoading: (state, loading) => { state.loading = loading; },
  setAiChatPanelOpen: (state, open) => { state.panelOpen = open; },
  setAiTripProfile: (state, { profile, rideId }) => {
    state.tripProfile = profile || {};
    state.tripProfileRideId = rideId;
  },
  setAiTripProfilePending: (state, pending) => { state.tripProfilePending = pending || []; },
  setAiChatAutoContinueInFlight: (state, inFlight) => { state.autoContinueInFlight = inFlight; },
  addAiChatMessage: (state, message) => { state.messages.push(message); },
};

export default { state, getters, actions, mutations };
